import { useCallback, useMemo, useRef, useState } from 'react';
import { HEARTBEAT_INTERVAL_MS, MONITOR_STATUS, trackWatchlistEvent, WATCHLIST_EVENTS } from '../constants';
import {
    createMonitor,
    deleteMonitor,
    disconnectMonitorSlack,
    listMonitors,
    MonitorApiError,
    patchMonitor,
    recheckAll,
    recheckMonitor,
} from '../service';
import useVisibilityAwarePolling from './useVisibilityAwarePolling';

const syncLiveRunsFromPoll = (prev, list) => {
    const next = {};
    list.forEach(monitor => {
        const run = prev[monitor.session_id];
        if (monitor.status === MONITOR_STATUS.RUNNING && monitor.active_run_id) {
            next[monitor.session_id] =
                run?.runId === monitor.active_run_id ? run : { runId: monitor.active_run_id, live: true };
        } else if (run && !run.live) {
            const settled = monitor.status !== MONITOR_STATUS.RUNNING && !monitor.claimed_at && !monitor.active_run_id;
            if (!settled) next[monitor.session_id] = run;
        }
    });
    return next;
};

const useMonitors = ({ attachRun, enabled }) => {
    const [monitors, setMonitors] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [liveRuns, setLiveRuns] = useState({});

    const monitorsRef = useRef(monitors);
    monitorsRef.current = monitors;
    const liveRunsRef = useRef(liveRuns);
    liveRunsRef.current = liveRuns;

    const applyServerMonitors = useCallback(
        list => {
            const next = syncLiveRunsFromPoll(liveRunsRef.current, list);
            setMonitors(list);
            setLiveRuns(next);
            list.forEach(monitor => {
                const run = next[monitor.session_id];
                const finishedLocally = run != null && !run.live && run.runId === monitor.active_run_id;
                if (monitor.active_run_id && !finishedLocally) {
                    attachRun(monitor.session_id, monitor.active_run_id);
                }
            });
        },
        [attachRun]
    );

    const fetchMonitors = useCallback(async () => {
        try {
            const list = await listMonitors();
            applyServerMonitors(list);
            setLoadError(null);
        } catch (error) {
            console.error('Failed to fetch monitors:', error);
            if (monitorsRef.current == null) setLoadError(error);
        }
    }, [applyServerMonitors]);

    const { forceFetch } = useVisibilityAwarePolling(fetchMonitors, HEARTBEAT_INTERVAL_MS, enabled);

    const onCheckVerdict = useCallback((sessionId, runId, verdict) => {
        setLiveRuns(prev => {
            const run = prev[sessionId];
            const base = run?.runId === runId ? run : { runId, live: true };
            return { ...prev, [sessionId]: { ...base, verdict } };
        });
    }, []);

    const onCheckFinish = useCallback((sessionId, runId, finishReason) => {
        setLiveRuns(prev => {
            const run = prev[sessionId];
            if (run?.runId !== runId) return prev;
            return { ...prev, [sessionId]: { ...run, live: false, finishReason } };
        });
    }, []);

    const mergeMonitor = useCallback(updated => {
        setMonitors(prev =>
            (prev ?? []).map(monitor =>
                monitor.monitor_id === updated.monitor_id
                    ? { ...updated, active_run_id: updated.active_run_id ?? monitor.active_run_id }
                    : monitor
            )
        );
    }, []);

    const markCheckStarted = useCallback((monitor, runId) => {
        setLiveRuns(prev => ({ ...prev, [monitor.session_id]: { runId, live: true } }));
    }, []);

    const addMonitor = useCallback(
        async (userQuery, schedule) => {
            const created = await createMonitor(userQuery, schedule);
            trackWatchlistEvent(WATCHLIST_EVENTS.ADD_MONITOR, { monitor_id: created.monitor_id, schedule });
            setMonitors(prev => [created, ...(prev ?? [])]);
            if (created.active_run_id) {
                markCheckStarted(created, created.active_run_id);
                attachRun(created.session_id, created.active_run_id);
            }
            return created;
        },
        [attachRun, markCheckStarted]
    );

    const savePrompt = useCallback(
        async (monitorId, userQuery) => {
            mergeMonitor(await patchMonitor(monitorId, { user_query: userQuery }));
            trackWatchlistEvent(WATCHLIST_EVENTS.EDIT_PROMPT, { monitor_id: monitorId });
        },
        [mergeMonitor]
    );

    const saveSchedule = useCallback(
        async (monitorId, schedule) => {
            mergeMonitor(await patchMonitor(monitorId, { schedule }));
            trackWatchlistEvent(WATCHLIST_EVENTS.CHANGE_SCHEDULE, { monitor_id: monitorId, schedule });
        },
        [mergeMonitor]
    );

    const setPaused = useCallback(
        async (monitorId, paused) => {
            mergeMonitor(await patchMonitor(monitorId, { paused }));
            trackWatchlistEvent(paused ? WATCHLIST_EVENTS.PAUSE_MONITOR : WATCHLIST_EVENTS.RESUME_MONITOR, {
                monitor_id: monitorId,
            });
        },
        [mergeMonitor]
    );

    const disconnectSlack = useCallback(
        async monitorId => {
            mergeMonitor(await disconnectMonitorSlack(monitorId));
            trackWatchlistEvent(WATCHLIST_EVENTS.SLACK_DISCONNECTED, { monitor_id: monitorId });
        },
        [mergeMonitor]
    );

    const recheck = useCallback(
        async monitor => {
            const updated = await recheckMonitor(monitor.monitor_id);
            trackWatchlistEvent(WATCHLIST_EVENTS.RUN_NOW, { monitor_id: monitor.monitor_id });
            mergeMonitor(updated);
            if (updated.active_run_id) {
                markCheckStarted(monitor, updated.active_run_id);
                attachRun(monitor.session_id, updated.active_run_id);
            }
        },
        [attachRun, markCheckStarted, mergeMonitor]
    );

    const recheckAllMonitors = useCallback(async () => {
        const { monitor_ids: monitorIds } = await recheckAll();
        trackWatchlistEvent(WATCHLIST_EVENTS.RUN_ALL, { count: monitorIds.length });
        setMonitors(prev =>
            (prev ?? []).map(m => (monitorIds.includes(m.monitor_id) ? { ...m, status: MONITOR_STATUS.QUEUED } : m))
        );
    }, []);

    const removeMonitorLocally = useCallback(monitorId => {
        const monitor = monitorsRef.current?.find(m => m.monitor_id === monitorId);
        setMonitors(prev => (prev ?? []).filter(m => m.monitor_id !== monitorId));
        if (!monitor) return;
        setLiveRuns(prev => {
            const { [monitor.session_id]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const removeMonitor = useCallback(
        async monitor => {
            try {
                await deleteMonitor(monitor.monitor_id);
            } catch (error) {
                if (!(error instanceof MonitorApiError) || error.status !== 404) throw error;
            }
            trackWatchlistEvent(WATCHLIST_EVENTS.DELETE_MONITOR, { monitor_id: monitor.monitor_id });
            removeMonitorLocally(monitor.monitor_id);
        },
        [removeMonitorLocally]
    );

    const liveState = useMemo(() => ({ liveRuns }), [liveRuns]);

    return {
        monitors,
        loadError,
        liveState,
        onCheckVerdict,
        onCheckFinish,
        fetchNow: forceFetch,
        addMonitor,
        savePrompt,
        saveSchedule,
        setPaused,
        disconnectSlack,
        recheck,
        recheckAllMonitors,
        removeMonitor,
        removeMonitorLocally,
    };
};

export default useMonitors;
